import { action, computed, observable } from 'mobx';
import _ from 'lodash';

import emailValidator from 'helpers/emailValidator';
import { post } from 'services/request';

export default class BulkRegistrationStore {
  fields = {};

  get mainFields() {
    return _.pickBy(this.fields, (field) => !field.additional);
  }
  get additionalFields() {
    return _.pickBy(this.fields, (field) => field.additional);
  }
  get hasAdditional() {
    return _.some(this.fields, (field) => field.additional);
  }

  columns = [];

  @observable items = observable([]);

  @computed get count() {
    return this.items.length;
  }

  @observable saving = false;
  @observable saved = false;
  @observable error = false;

  savingAbortController; // = new AbortController()

  @observable successfulRegistrations = observable([]);
  @observable failedRegistrations = observable([]);

  constructor(user, sale, round, usedEmails, t) {
    this.successfullyRegistered = observable([]);
    this.failedRegistrations = observable([]);

    this.isItemEmpty = this.isItemEmpty.bind(this);
    this.isItemValid = this.isItemValid.bind(this);

    this.user = user;
    this.sale = sale;
    this.round = round;
    this.usedEmails = usedEmails;
    this.t = t;

    const config = sale.root.saleConfig;

    this.fields = config.getRegistrationScheme.call(config, sale, t);

    this.columns = [
      {
        key: '_validation',
        width: 25,
        minWidth: 25,
      },
    ];

    if (config.bulkRegistrationEnableCheckboxes)
      this.columns.push({ key: '_select', width: 30, minWidth: 30 });
    else this.columns.push({ key: '_remove', width: 30, minWidth: 30 });

    _.forOwn(this.mainFields, (definition, fieldName) => {
      this.columns.push({
        key: fieldName,
        fieldName,
        ...this.getColumnDefaultProps(definition),
        title: t(`field::${fieldName}`),
        ...definition,
      });
    });

    if (this.hasAdditional) {
      this.columns.push({
        key: '_additional',
        width: 40,
        minWidth: 40,
      });
    }

    this.columns = _.concat(
      this.columns,
      config.uploadTypes.map((type, index) => {
        return {
          key: `uploadtype-${type}`,
          title: t(`uploadtype::${type}`).substring(0, 1),
          required: config.requiredUploadTypes.includes(type),
          index: index,
          width: 60,
          minWidth: 60,
          footnote: t(`uploadtype::${type}`),
        };
      })
    );

    if (config.enableOptionalUploads) {
      this.columns = _.concat(
        this.columns,
        config.uploadTypes.map((type, index) => {
          return {
            key: `uploadtype-${type}`,
            title: t(`uploadtype::${type}`).substring(0, 1),
            optional: true,
            index: index,
            width: 60,
            minWidth: 60,
            footnote: t(`uploadtype::${type}`) + ` (${t('Optional')})`,
          };
        })
      );
    }

    // assign footnotes indexes
    let footnote = 0;
    this.columns.forEach((column) => {
      column.footnote_index = column.footnote ? ++footnote : 0;
    });
  }

  getColumnDefaultProps(definition) {
    switch (definition.type) {
      case 'combobox':
      case 'dropdown':
        return {
          width: 100,
          minWidth: 60,
        };
      case 'checkbox':
        return {
          width: 40,
          minWidth: 40,
        };
      default:
        return {
          width: 200,
          minWidth: 100,
        };
    }
  }

  reset() {
    this.successfullyRegistered.clear();
    this.failedRegistrations.clear();
    this.items.clear();
    this.saving = false;
    this.saved = false;
    this.error = false;
  }

  abort() {
    if (this.savingAbortController) this.savingAbortController.abort();
    this.savingAbortController = null;
  }

  @action async save() {
    const indexes = []; // indexes of valid items
    this.items.forEach((item) => {
      item._error = '';
      item._remove = false;
    });

    this.saved = false;
    this.error = false;

    const validItems = this.validItems;
    validItems.forEach((item) => indexes.push(item._index));

    this.savingAbortController =
      typeof window.AbortController !== 'undefined'
        ? new window.AbortController()
        : null;

    const config = this.sale.root.saleConfig;

    this.saving = true;
    this.successfullyRegistered.clear();

    try {
      const { isMls, saleAgentData } = this.sale.root;
      const offers = validItems.map((item) => {
        // start with the base empty offer
        const offer = config.createOffer.call(config, this.sale, this.round);

        // construct input offer values from the input row (=item)
        const inputOffer = {
          buyerData: {},
          offerData: {
            //TODO: offer_viewing: '',
            optional_uploads: item.optionalUploads,
            required_uploads: item.requiredUploads,
          },
          agentData: isMls ? this.user.agentData() : saleAgentData,
        };

        if (item.integrations) inputOffer.integrations = item.integrations;
        /* example
          integrations = {
            onoffice: {
              id: 39,
              customer_name: "AuctionTech"
            }
          }
          */

        // process each field
        _.forOwn(this.fields, (field, key) => {
          const targetArray =
            typeof field.target === 'undefined'
              ? [key]
              : Array.isArray(field.target)
              ? field.target
              : [field.target];
          const value =
            typeof field.transform === 'function'
              ? field.transform(item[key], item) // transform the value if needed
              : item[key];

          if (!!value || !field.additional) {
            // do not send empty additional fields
            targetArray.forEach((target) => {
              _.set(inputOffer, target, value);
            });
          }
        });

        // merge it
        return _.merge(offer, inputOffer);
      });

      const response = await post(
        `/api/v2/registrations/bulk/${this.sale.root.saleId}`,
        offers,
        this.sale.root.user.accessToken,
        this.savingAbortController ? this.savingAbortController.signal : null
      );

      if (
        !this.savingAbortController ||
        !this.savingAbortController.signal.aborted
      ) {
        // response = [{ successful: true|false, error: '' }, ...]
        //console.log(response);
        response.forEach((itemResponse, responseIndex) => {
          const index = indexes[responseIndex];
          if (itemResponse.successful) {
            this.items[index]._remove = true;
            this.usedEmails.push(this.items[index].email); // add emails of successfully registered customers
            this.successfulRegistrations.push(this.items[index]);
          } else {
            this.items[index]._error = itemResponse.error || 'unknown';
            this.error = true;
            this.failedRegistrations.push(this.items[index]);
          }
        });

        let i = 0;
        while (i < this.items.length)
          if (this.items[i]._remove) this.remove(i);
          else i++;
      }
    } finally {
      this.saving = false;
      this.saved = true;
      this.savingAbortController = null;

      if (this.onSave) this.onSave();
    }
  }

  agentData() {
    const { user } = this.root;
    const { name, family_name, given_name } = user.claims;
    const userName = family_name ? `${given_name} ${family_name}` : name;

    return {
      id: user.claims.agent_id,
      name: userName,
      signature: user.claims.signature || userName,
      email: user.claims.email,
      phone: user.claims.phone_number,
      locale: user.claims.locale,
      agency_name: user.claims.agency_name,
      agency_street: user.claims.agency_street,
      agency_house_number: user.claims.agency_house_number,
      agency_zip: user.claims.agency_zip,
      agency_city: user.claims.agency_city,
      office_id: user.claims.office_ids_allowed,
      sub: user.claims.sub,
      tenant: user.claims.tenant,
    };
  }

  isItemEmpty(item) {
    return !_.find(
      this.columns,
      (column) => column.fieldName && item[column.fieldName] === ''
    );
  }

  isItemValid(item) {
    // check required fields
    return this.isItemComplete(item) && !this.isItemDuplicate(item);
  }

  isItemComplete(item) {
    // check required fields
    if (
      _.find(
        Object.keys(this.fields),
        (column) => this.fields[column].required && !item[column]
      )
    )
      return false;

    // check email validity
    if (!emailValidator(item.email)) return false;

    return true;
  }

  isItemDuplicate(item) {
    if (this.usedEmails && this.usedEmails.includes(item.email)) return true;

    return _.find(
      this.items,
      (_item, _index) =>
        _item.email &&
        item.email &&
        _item.email.toLowerCase() === item.email.toLowerCase() &&
        _item._index < item._index
    );
  }

  get itemColorMap() {
    return {
      valid: '#dff6dd',
      duplicate: '#ffff99',
      invalid: '#ffb3b3',
    };
  }

  @computed get validItems() {
    return _.filter(
      this.items,
      (item) => item._selected && this.isItemValid(item)
    );
  }

  @computed get validItemsCount() {
    let count = 0;
    this.items.forEach((item) => {
      if (item._selected && this.isItemValid(item)) count++;
    });
    return count;
  }

  @action clear() {
    this.items.clear();
  }

  @action add(item = {}) {
    _.forOwn(this.fields, (field, key) => {
      if (!item[key]) {
        item[key] = '';
        if (field.required) {
          if (field.default) item[key] = field.default;
          else if (field.options && field.options.length > 0)
            item[key] = field.options[0];
        }
      }
      // for enumerations try to translate from localized values to keys
      if (item[key] && field.options) {
        const _value = item[key].toString().toUpperCase();
        field.options.forEach((option) => {
          if (
            _value === option.toString().toUpperCase() ||
            _value ===
              this.t(
                `${field.localizationPrefixOverride || key}::${option}`
              ).toUpperCase()
          )
            item[key] = option;
        });
      }
    });

    if (typeof item.optionalUploads === 'undefined') item.optionalUploads = [];
    if (typeof item.requiredUploads === 'undefined') item.requiredUploads = [];

    if (typeof item._selected === 'undefined') item._selected = true;
    item._error = '';
    item._index = this.items.length;

    let _item = observable(item);
    this.items.push(_item);
  }

  @action remove(index) {
    this.items.splice(index, 1);
    this.items.forEach((item, index) => {
      item._index = index;
    });
  }

  @action change(index, fieldName, value) {
    this.items[index][fieldName] = value;
  }

  @action changeUploads(index, type, optional, isSelected) {
    this._changeUploads(index, type, optional, isSelected);
    if (isSelected) this._changeUploads(index, type, !optional, !isSelected);
  }

  @action _changeUploads(index, type, optional, isSelected) {
    const uploads = optional
      ? this.items[index].optionalUploads
      : this.items[index].requiredUploads;
    const i = uploads.indexOf(type);
    if (isSelected) {
      if (i < 0) uploads.push(type);
    } else if (i >= 0) uploads.splice(i, 1);
  }
}



// WEBPACK FOOTER //
// ./src/app/containers/Sale/OffersSection/BulkRegistration/BulkRegistrationStore.jsx